import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../AppContext';
import { sendPlayerState, subscribeToPlayerState } from '../../services/gameService';
import MobileControls from './games/synaptic-stacking/MobileControls';

// --- Constants ---
const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 20;

const TETROMINOS = {
  0: { shape: [[0]], color: 'bg-transparent' },
  I: { shape: [[0, 'I', 0, 0], [0, 'I', 0, 0], [0, 'I', 0, 0], [0, 'I', 0, 0]], color: 'bg-cyan-400 border-cyan-200' },
  J: { shape: [[0, 'J', 0], [0, 'J', 0], ['J', 'J', 0]], color: 'bg-blue-600 border-blue-400' },
  L: { shape: [[0, 'L', 0], [0, 'L', 0], [0, 'L', 'L']], color: 'bg-orange-500 border-orange-300' },
  O: { shape: [['O', 'O'], ['O', 'O']], color: 'bg-yellow-400 border-yellow-200' },
  S: { shape: [[0, 'S', 'S'], ['S', 'S', 0], [0, 0, 0]], color: 'bg-green-500 border-green-300' },
  T: { shape: [[0, 0, 0], ['T', 'T', 'T'], [0, 'T', 0]], color: 'bg-purple-600 border-purple-400' },
  Z: { shape: [['Z', 'Z', 0], [0, 'Z', 'Z'], [0, 0, 0]], color: 'bg-red-600 border-red-400' },
};

const createBoard = () => Array.from({ length: BOARD_HEIGHT }, () => new Array(BOARD_WIDTH).fill([0, 'empty']));

const SCORING_POINTS = { 1: 40, 2: 100, 3: 300, 4: 1200 };

// --- Helper Functions ---
const randomTetromino = () => {
  const tetrominos = 'IJLOSTZ';
  const randTetromino = tetrominos[Math.floor(Math.random() * tetrominos.length)];
  return TETROMINOS[randTetromino];
};

const checkCollision = (player, board, { x: moveX, y: moveY }) => {
  for (let y = 0; y < player.tetromino.length; y += 1) {
    for (let x = 0; x < player.tetromino[y].length; x += 1) {
      if (player.tetromino[y][x] !== 0) {
        if (
          !board[y + player.pos.y + moveY] ||
          !board[y + player.pos.y + moveY][x + player.pos.x + moveX] ||
          board[y + player.pos.y + moveY][x + player.pos.x + moveX][1] === 'merged'
        ) {
          return true;
        }
      }
    }
  }
  return false;
};

// --- UI Helper Components ---
const Cell = React.memo(({ type }) => {
    const color = TETROMINOS[type].color;
    const baseStyle = 'w-full h-full aspect-square';
    if (type === 0) {
        return <div className={`${baseStyle} bg-slate-800/50`}></div>;
    }
    return <div className={`${baseStyle} ${color} border-2 border-b-4 border-r-4 rounded-md`}></div>;
});

const GameBoard = ({ board }) => (
  <div 
    className="w-full h-full grid grid-cols-10 grid-rows-20 gap-px p-2 bg-slate-900/70 border-4 border-slate-700 rounded-lg shadow-lg"
    style={{ aspectRatio: '1 / 2' }}
  >
    {board.map((row, y) => row.map((cell, x) => <Cell key={`${y}-${x}`} type={cell[0]} />))}
  </div>
);

const NextPiecePreview = ({ nextTetromino }) => {
    const { t } = useTranslation();
    const grid = Array.from({ length: 4 }, () => Array(4).fill(0));
    if (nextTetromino) {
        nextTetromino.shape.forEach((row, y) => {
            row.forEach((value, x) => {
                if (value !== 0) {
                    grid[y][x] = value;
                }
            });
        });
    }

    return (
        <div className="w-full p-2 md:p-4 bg-slate-900/70 rounded-lg border-2 border-slate-700">
            <h2 className="text-xs md:text-sm font-bold text-cyan-300 mb-2 uppercase tracking-widest text-center">{t('synapticStacking.next')}</h2>
            <div className="grid grid-cols-4 grid-rows-4 gap-px mx-auto w-full aspect-square max-w-[6rem]">
                {grid.map((row, y) => row.map((cellValue, x) => (
                    <div key={`${y}-${x}`} className={`w-full h-full ${cellValue !== 0 && nextTetromino ? TETROMINOS[cellValue].color : 'bg-slate-800/50'} rounded-sm`}></div>
                )))}
            </div>
        </div>
    );
}

const GameStatsDisplay = ({ stats }) => {
    const { t } = useTranslation();
    return (
        <div className="w-full p-2 md:p-4 bg-slate-900/70 rounded-lg border-2 border-slate-700 space-y-2 md:space-y-3">
            <div>
                <h2 className="text-xs md:text-sm font-bold text-cyan-300 uppercase tracking-widest">{t('synapticStacking.score')}</h2>
                <p className="text-xl md:text-2xl text-white font-mono">{stats.score}</p>
            </div>
            <div>
                <h2 className="text-xs md:text-sm font-bold text-cyan-300 uppercase tracking-widest">{t('synapticStacking.lines')}</h2>
                <p className="text-xl md:text-2xl text-white font-mono">{stats.lines}</p>
            </div>
            <div>
                <h2 className="text-xs md:text-sm font-bold text-cyan-300 uppercase tracking-widest">{t('synapticStacking.level')}</h2>
                <p className="text-xl md:text-2xl text-white font-mono">{stats.level}</p>
            </div>
        </div>
    );
};

const Modal = ({ title, children }) => (
    <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-50">
        <div className="bg-slate-800 border-4 border-cyan-400 p-4 md:p-8 rounded-lg shadow-2xl text-center text-white max-w-sm mx-auto">
            <h1 className="text-2xl md:text-4xl font-bold mb-4 text-cyan-300">{title}</h1>
            {children}
        </div>
    </div>
);

// --- Custom Hooks ---
const useInterval = (callback, delay) => {
    const savedCallback = useRef(callback);
  
    useEffect(() => {
      savedCallback.current = callback;
    }, [callback]);
  
    useEffect(() => {
      function tick() {
        savedCallback.current();
      }
      if (delay !== null) {
        const id = setInterval(tick, delay);
        return () => clearInterval(id);
      }
    }, [delay]);
};

// --- Main Game Component ---
const SynapticStacking = ({ dreamWeaveId, nexusId, souls, isSpectatorMode = false, onGameSubmit, onGameOver, isRemoteMode = false, syncId, myRole, remoteRole, autoStart = false }) => {
    const { t } = useTranslation();
    const { appFunctions } = useAppContext();
    const [board, setBoard] = useState(createBoard());
    const [player, setPlayer] = useState({
        pos: { x: 0, y: 0 },
        tetromino: TETROMINOS[0].shape,
        collided: false,
    });
    const [nextTetromino, setNextTetromino] = useState(null);
    const [stats, setStats] = useState({ score: 0, lines: 0, level: 0 });
    const [dropTime, setDropTime] = useState(null);
    const [isGameOver, setGameOver] = useState(false);
    const [isPaused, setPaused] = useState(true);
    const [isStarted, setStarted] = useState(false);
    const [gameLog, setGameLog] = useState([]);
    const [submissionStatus, setSubmissionStatus] = useState('idle'); // idle, submitting, submitted, error
    const [lastSoulsAwarded, setLastSoulsAwarded] = useState(0);
    
    const gameAreaRef = useRef(null);
    const aiState = useRef({
        isThinking: false,
        moveSequence: [],
        targetRotation: 0,
        targetX: 0,
    });

    const resetPlayer = useCallback(() => {
        const newTetromino = nextTetromino || randomTetromino();
        const newNextTetromino = randomTetromino();

        const newPlayerState = {
            pos: { x: BOARD_WIDTH / 2 - 1, y: 0 },
            tetromino: newTetromino.shape,
            collided: false,
        };

        setPlayer(newPlayerState);
        setNextTetromino(newNextTetromino);
        
        aiState.current.moveSequence = []; // Clear AI moves on new piece

        if (checkCollision(newPlayerState, board, { x: 0, y: 0 })) {
             setGameOver(true);
             setDropTime(null);
        }
    }, [board, nextTetromino]);
    
    useEffect(() => {
        if (isStarted && !nextTetromino) {
           resetPlayer();
        }
    }, [isStarted, nextTetromino, resetPlayer]);

    // --- Sync (Remote Mode) & AutoStart ---
    useEffect(() => {
        if (autoStart && !isStarted && !isRemoteMode) {
            startGame();
        }

        if (isRemoteMode && syncId && remoteRole) {
            const unsubscribe = subscribeToPlayerState(syncId, remoteRole, (data) => {
                if (data) {
                    if (data.board) setBoard(data.board);
                    if (data.player) setPlayer(data.player);
                    if (data.score !== undefined) setStats(prev => ({ ...prev, score: data.score }));
                    
                    // Auto-start if idle
                    if (!isStarted) {
                        setStarted(true);
                        setPaused(false);
                    }
                }
            });
            return () => unsubscribe();
        }
    }, [isRemoteMode, syncId, remoteRole, isStarted, autoStart]);

    const startGame = () => {
        setBoard(createBoard());
        setStats({ score: 0, lines: 0, level: 0 });
        setGameLog([]);
        setSubmissionStatus('idle');
        setLastSoulsAwarded(0);
        setNextTetromino(randomTetromino());
        resetPlayer();
        setGameOver(false);
        setPaused(false);
        setStarted(true);
        setDropTime(1000);
        if (gameAreaRef.current) gameAreaRef.current.focus();
    };

    const updatePlayerPos = ({ x, y, collided }) => {
        setPlayer(prev => {
            const newState = {
                ...prev,
                pos: { x: prev.pos.x + x, y: prev.pos.y + y },
                collided: collided !== undefined ? collided : prev.collided,
            };
            
            if (!isSpectatorMode && !isRemoteMode && syncId && myRole) {
                // We send board state too since it changes on collision/merge
                // Ideally throttle this or only send board on merge
                const stateToSend = { 
                    player: newState,
                    score: stats.score,
                    // Only send board if collided (merged) to save bandwidth? 
                    // For simplicity, maybe just send board occasionally or check if it changed.
                    // Actually, let's send board only when collided is true, otherwise just player.
                    ...(collided ? { board } : {}) 
                };
                sendPlayerState(syncId, myRole, stateToSend);
            }
            return newState;
        });
    };
    
    const rotate = (matrix) => {
        const rotatedTetromino = matrix.map((_, index) => matrix.map(col => col[index]));
        return rotatedTetromino.map(row => row.reverse());
    };

    const playerRotate = useCallback(() => {
        if (isPaused || isGameOver) return;
        const clonedPlayer = JSON.parse(JSON.stringify(player));
        clonedPlayer.tetromino = rotate(clonedPlayer.tetromino);

        const pos = clonedPlayer.pos.x;
        let offset = 1;
        while (checkCollision(clonedPlayer, board, { x: 0, y: 0 })) {
            clonedPlayer.pos.x += offset;
            offset = -(offset + (offset > 0 ? 1 : -1));
            if (offset > clonedPlayer.tetromino[0].length) {
                clonedPlayer.pos.x = pos; // Reset if cannot fit
                return;
            }
        }
        setPlayer(clonedPlayer);
    }, [player, board, isPaused, isGameOver]);

    const movePlayer = useCallback((dir) => {
        if (isPaused || isGameOver) return;
        if (!checkCollision(player, board, { x: dir, y: 0 })) {
            updatePlayerPos({ x: dir, y: 0 });
        }
    }, [player, board, isPaused, isGameOver]);

    const drop = useCallback(() => {
        if (isPaused || isGameOver) return;
        if (!checkCollision(player, board, { x: 0, y: 1 })) {
            updatePlayerPos({ x: 0, y: 1 });
        } else {
            if (player.pos.y < 1) {
                setGameOver(true);
                setDropTime(null);
            }
            updatePlayerPos({ x: 0, y: 0, collided: true });
        }
    }, [player, board, isPaused, isGameOver]);

    const hardDrop = useCallback(() => {
        if (isPaused || isGameOver) return;
        let y = 0;
        while (!checkCollision(player, board, { x: 0, y: y + 1 })) {
            y++;
        }
        updatePlayerPos({ x: 0, y: y, collided: true });
    }, [player, board, isPaused, isGameOver]);

    const handleKeyDown = (e) => {
        e.preventDefault();
        if (isGameOver || isPaused || isSpectatorMode) return;

        if (e.key === 'ArrowLeft') movePlayer(-1);
        else if (e.key === 'ArrowRight') movePlayer(1);
        else if (e.key === 'ArrowDown') setDropTime(50); // Soft drop
        else if (e.key === 'ArrowUp') playerRotate();
        else if (e.key === ' ' || e.key === 'Spacebar') hardDrop();
        else if (e.key.toLowerCase() === 'p') togglePause();
    };

    const handleKeyUp = (e) => {
        if (!isGameOver && !isPaused && e.key === 'ArrowDown') {
            setDropTime(1000 / (stats.level + 1));
        }
    };

    const togglePause = useCallback(() => {
        if (isGameOver || !isStarted) return;
        setPaused(prev => {
            if (prev) {
                setDropTime(1000 / (stats.level + 1));
                if (gameAreaRef.current) gameAreaRef.current.focus();
            } else {
                setDropTime(null);
            }
            return !prev;
        });
    }, [isGameOver, isStarted, stats.level]);

    useEffect(() => {
        const sweepRows = (newBoard) => {
          let clearedCount = 0;
          const sweptBoard = newBoard.reduce((acc, row) => {
            if (row.findIndex(cell => cell[0] === 0) === -1) {
              clearedCount++;
              acc.unshift(new Array(BOARD_WIDTH).fill([0, 'empty']));
              return acc;
            }
            acc.push(row);
            return acc;
          }, []);
          
          if (clearedCount > 0) {
            setStats(prev => {
                const newLines = prev.lines + clearedCount;
                const newLevel = Math.floor(newLines / 10);
                const newScore = prev.score + SCORING_POINTS[clearedCount] * (prev.level + 1);
                setDropTime(1000 / (newLevel + 1));
                return { score: newScore, lines: newLines, level: newLevel };
            });
          }
          return sweptBoard;
        };

        if (player.collided) {
            // Log state *before* merging the piece for training data
            if (!isSpectatorMode) {
                const logEntry = {
                    board: board.map(row => row.map(cell => (cell[1] === 'merged' ? 1 : 0))),
                    piece: player.tetromino,
                    position: player.pos,
                    score: stats.score,
                    lines: stats.lines,
                    level: stats.level,
                };
                setGameLog(prevLog => [...prevLog, logEntry]);
            }

            const newBoard = JSON.parse(JSON.stringify(board));
            player.tetromino.forEach((row, y) => {
                row.forEach((value, x) => {
                    if (value !== 0) {
                        newBoard[y + player.pos.y][x + player.pos.x] = [value, 'merged'];
                    }
                });
            });
            setBoard(sweepRows(newBoard));
            resetPlayer();
        }
    }, [player.collided, board, player.pos.x, player.pos.y, player.tetromino, resetPlayer, isSpectatorMode, stats]);
    
    useInterval(drop, dropTime);

    // --- Game Submission Logic ---
    useEffect(() => {
        if (isGameOver && !isSpectatorMode && submissionStatus === 'idle') {
            const submit = async () => {
                setSubmissionStatus('submitting');
                try {
                    if (onGameSubmit) {
                        const data = await onGameSubmit('synapticStacking', stats.score, gameLog);
                        if (data && data.success) {
                            setLastSoulsAwarded(data.soulsAwarded || 0);
                        }
                    }
                    setSubmissionStatus('submitted');
                } catch (err) {
                    console.error("Error submitting game score:", err);
                    setSubmissionStatus('error');
                }
            };
            submit();
        }
    }, [isGameOver, isSpectatorMode, submissionStatus, onGameSubmit, stats.score, gameLog]);

    // --- AI Spectator Logic ---
    useEffect(() => {
        if (!isSpectatorMode || isGameOver || isPaused || !isStarted) return;

        const MAX_SOULS_FOR_SKILL = 20000;
        const skill = Math.min(1, (souls || 0) / MAX_SOULS_FOR_SKILL);
        
        // Higher skill = faster thinking
        const thinkSpeed = 150 + (1 - skill) * 850; // From 1000ms down to 150ms

        const aiThinkLoop = async () => {
            if (aiState.current.isThinking || player.collided) return;

            // 1. Do we have a plan? If so, execute the next step.
            if (aiState.current.moveSequence.length > 0) {
                const nextMove = aiState.current.moveSequence.shift();
                if (nextMove === 'rotate') playerRotate();
                else if (nextMove === 'left') movePlayer(-1);
                else if (nextMove === 'right') movePlayer(1);
                else if (nextMove === 'drop') hardDrop();
                return;
            }

            // 2. If no plan, make one.
            aiState.current.isThinking = true;
            try {
                const getAiMoveCloudFn = httpsCallable(appFunctions, 'getAiGameMove');
                const result = await getAiMoveCloudFn({
                    gameType: 'synapticStacking',
                    dreamWeaveId,
                    souls,
                    gameState: {
                        board: board.map(row => row.map(cell => (cell[1] === 'merged' ? 1 : 0))),
                        currentPiece: { shape: player.tetromino, x: player.pos.x, y: player.pos.y },
                        nextPiece: nextTetromino ? { shape: nextTetromino.shape } : null,
                    },
                });
                
                const { rotation, x: targetX } = result.data.action;
                
                // Generate a sequence of moves to get to the target state
                const newSequence = [];
                // Rotations first
                for (let i = 0; i < rotation; i++) {
                    newSequence.push('rotate');
                }
                // Then horizontal movements
                const currentX = player.pos.x;
                const diffX = targetX - currentX;
                for (let i = 0; i < Math.abs(diffX); i++) {
                    newSequence.push(diffX > 0 ? 'right' : 'left');
                }
                newSequence.push('drop');
                aiState.current.moveSequence = newSequence;

            } catch (error) {
                console.error("Error fetching AI move:", error);
                // Fallback: generate a simple random move sequence
                const randomMoves = ['left', 'right', 'rotate'];
                aiState.current.moveSequence = [randomMoves[Math.floor(Math.random() * 3)]];
            } finally {
                aiState.current.isThinking = false;
            }
        };

        const intervalId = setInterval(aiThinkLoop, thinkSpeed);
        return () => clearInterval(intervalId);

    }, [isSpectatorMode, isGameOver, isPaused, isStarted, souls, player, board, nextTetromino, appFunctions, dreamWeaveId, playerRotate, movePlayer, hardDrop]);


    const displayBoard = useMemo(() => {
        const newBoard = JSON.parse(JSON.stringify(board));
        player.tetromino.forEach((row, y) => {
            row.forEach((value, x) => {
                if (value !== 0) {
                    const boardY = y + player.pos.y;
                    const boardX = x + player.pos.x;
                    if (boardY >= 0 && boardY < BOARD_HEIGHT && boardX >= 0 && boardX < BOARD_WIDTH) {
                        newBoard[boardY][boardX] = [value, 'empty'];
                    }
                }
            });
        });
        return newBoard;
    }, [player, board]);

    return (
        <div 
            className="w-full min-h-screen flex flex-col items-center justify-center p-2 sm:p-4 bg-slate-900 text-white"
            role="button" 
            tabIndex={0} 
            onKeyDown={handleKeyDown} 
            onKeyUp={handleKeyUp}
            ref={gameAreaRef}
        >
            <div className="flex flex-col md:flex-row gap-4 items-start w-full max-w-4xl mx-auto">
                
                {/* Game Board */}
                <div className="relative w-full md:w-auto md:flex-1 flex justify-center">
                    <div className="w-full max-w-sm">
                        <GameBoard board={displayBoard} />
                    </div>
                    {!isStarted && (
                        <Modal title={t('synapticStacking.title')}>
                            <button onClick={startGame} className="mt-4 px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold rounded-lg text-xl shadow-lg transition-transform transform hover:scale-105">
                                {t('synapticStacking.startGame')}
                            </button>
                        </Modal>
                    )}
                    {isGameOver && (
                        <Modal title={t('synapticStacking.gameOver.title', 'Game Over')}>
                            {submissionStatus === 'submitting' && <p className="text-lg md:text-xl text-cyan-300">{t('synapticStacking.gameOver.submitting', 'Submitting...')}</p>}
                            {submissionStatus === 'error' && <p className="text-lg md:text-xl text-red-400">{t('synapticStacking.gameOver.error', 'Error submitting score')}</p>}
                            {submissionStatus === 'submitted' && (
                                <>
                                    <p className="text-lg md:text-xl">{t('synapticStacking.gameOver.finalScore', 'Final Score')}: <span className="font-mono text-yellow-300">{stats.score}</span></p>
                                    <p className="text-xl md:text-2xl mt-4">{t('synapticStacking.gameOver.soulsAwarded', 'Souls Awarded')}: <span className="font-mono text-purple-300">{lastSoulsAwarded}</span></p>
                                    
                                    <div className="mt-4 p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-center">
                                        <p className="text-sm text-red-200 uppercase tracking-widest font-bold mb-1">Raid Boss Damage</p>
                                        <p className="text-3xl font-orbitron text-red-400">+{Math.floor(stats.score * 8)}</p>
                                    </div>
                                    
                                    <button onClick={startGame} className="mt-8 px-6 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded">
                                        {t('synapticStacking.gameOver.playAgain', 'Play Again')}
                                    </button>
                                </>
                            )}
                        </Modal>
                    )}
                     {isPaused && !isGameOver && isStarted && (
                        <Modal title={t('synapticStacking.paused.title')}>
                             <button onClick={togglePause} className="mt-4 px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold rounded-lg text-xl shadow-lg transition-transform transform hover:scale-105">
                                {t('synapticStacking.paused.resume')}
                            </button>
                        </Modal>
                    )}
                </div>

                {/* Right Sidebar */}
                <aside className="w-full md:w-40 lg:w-48 flex flex-row md:flex-col gap-4">
                    <div className="flex-1 md:flex-none">
                        {nextTetromino && <NextPiecePreview nextTetromino={nextTetromino}/>}
                    </div>
                    <div className="flex-1 md:flex-none">
                         <GameStatsDisplay stats={stats} />
                    </div>
                    <div className="hidden md:block">
                        <button onClick={togglePause} className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-cyan-300 font-bold rounded-lg shadow-md transition">
                            {isPaused ? t('synapticStacking.resume') : t('synapticStacking.pause')}
                        </button>
                    </div>
                </aside>
            </div>
            
            {/* Mobile Controls */}
            {!isSpectatorMode && isStarted && !isGameOver && (
                <MobileControls 
                    onMove={movePlayer}
                    onRotate={playerRotate}
                    onDrop={hardDrop}
                    onPause={togglePause}
                />
            )}
        </div>
    );
};

export default SynapticStacking;